import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ModalController, NavParams } from '@ionic/angular';
import { AgreementsService } from 'src/app/pages/agreements/services/agreements.service';
import { AgreementSupportTerms } from 'src/app/pages/agreements/types/agreement-create-update-form.model';
import { BasePage } from 'src/app/pages/base.page';
import { TermsAndConditionsComponent } from 'src/app/shared/components/terms-and-conditions-modal/terms-and-conditions-modal.component';
import { Agreement } from 'src/app/shared/models/gql/agreement.model';
import { Organisation } from 'src/app/shared/models/gql/organisation.model';
import { User } from 'src/app/shared/models/gql/user.model';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { getGraphQLErrorText } from 'src/app/shared/utilities/graphql-error-handling';

@Component({
  selector: 'app-agreement-form-step4',
  templateUrl: './agreement-form-step4.component.html',
  styleUrls: ['./agreement-form-step4.component.scss'],
})
export class AgreementFormStep4Component extends BasePage implements OnInit {
  public form: UntypedFormGroup;

  private statesForNew: Array<string> = ['requested', '', null, undefined];

  // Last created agreement
  private lastAgreement: Agreement;

  // Agreement details
  private agreement: Agreement;

  // Stores agreement status. True if it is a new agreement and false if it isn't.
  public isNewAgreement: boolean;

  // Boolean indicating wether form should be pre-filled or not
  protected isPreFilled = false;

  // Coordinator data for agreement
  protected otherUser: Partial<User> | object;

  constructor(
    private navParams: NavParams,
    private agreementsService: AgreementsService,
    private utilityService: UtilityService,
    protected modalCtrl: ModalController,
  ) {
    super('AgreementFormStepfComponent');
    const currentAgreement = this.navParams.get('agreement');
    this.agreement = this.amendInfoFor(currentAgreement);
    this.isNewAgreement = this.statesForNew.includes(this.agreement.status);

    this.setupForm();
  }

  async ngOnInit() {
    if (this.agreement.id) {
      // Get user information from existing agreement
      await this.getOtherUser(this.agreement.id);
    } else {
      // Get user information from step 3 if new agreement
      this.otherUser = this.agreement.otherUser;
    }
  }

  /**
   * Set the support terms values on the form to the values emitted from the agreement support terms form.
   *
   * @param supportTerms - the support terms emitted from the agreement travel form.
   */
  onSupportTermsChange(supportTerms: AgreementSupportTerms): void {
    this.form.patchValue(supportTerms);
  }

  /**
   * Retrieve active agreements and save the last one to lastAgreement
   */
  private async getLastAgreement(): Promise<void> {
    const list = await this.agreementsService.getActiveAgreementList('myClients');
    if (list.myClients?.nodes?.length > 0) {
      this.lastAgreement = list.myClients.nodes[list.myClients.nodes.length - 1];
    }
  }

  /**
   * Fetch other user information
   *
   * @param agreementID - ID of agreement
   */
  private async getOtherUser(agreementID: number | string) {
    this.otherUser = await this.agreementsService.getOtherUser(agreementID);
  }

  /**
   * Show prefill agreement banner if support terms exist
   *
   * @param terms - Agreement support terms
   */
  private setPrefillAgreementBanner(terms?: Agreement): void {
    if (terms.supportCancel || terms.supportChange || terms.supportMinHours || terms.supportFeedback) {
      this.isPreFilled = true;
    }
  }

  /**
   * Set up form group and pre-populate if a previous agreement exists
   */
  private async setupForm(): Promise<void> {
    try {
      if (this.isNewAgreement) {
        await this.getLastAgreement();
        if (this.lastAgreement && this.lastAgreement.id) {
          const supportTerms = await this.getAgreementSupportTerms(this.lastAgreement.id);
          this.populateAgreementTermsForm(supportTerms);
          this.setPrefillAgreementBanner(supportTerms);
        } else {
          this.populateAgreementTermsForm();
        }
      } else {
        // For amend/edit the agreements
        const agreementTerms = await this.getAgreementSupportTerms(this.agreement.id);
        if (agreementTerms?.agreementAmendment) {
          // only proceed this condition when there is the previous amendment agreement available
          this.populateAgreementTermsForm(agreementTerms.agreementAmendment);
        } else {
          this.populateAgreementTermsForm(agreementTerms);
        }
      }
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * To get the agreement terms like support cancel,change, min hours from services.
   *
   * @param id - Agreement id.
   * @returns - Agreement terms data.
   */
  private async getAgreementSupportTerms(id: number | string): Promise<Agreement> {
    try {
      return await this.agreementsService.getAgreementSupportTermsById(id);
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * To update the agreement terms form.
   *
   * @param terms - Agreement terms like support cancel,change, min hours.
   */
  private populateAgreementTermsForm(terms?: Agreement): void {
    this.form = new UntypedFormGroup({
      supportCancel: new UntypedFormControl(terms?.supportCancel || ''),
      supportChange: new UntypedFormControl(terms?.supportChange || ''),
      supportMinHours: new UntypedFormControl(terms?.supportMinHours || ''),
      supportFeedback: new UntypedFormControl(terms?.supportFeedback || ''),
    });
  }

  /**
   * Submit form
   */
  public async submit(): Promise<void> {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      const terms = await this.agreementsService.getOrganisationTerms(this.agreement.otherUser?.id);
      // continue to show popup for accepting terms and conditions only when
      // terms are not already accepted,
      // account type is 'Consumer'and client is co-ordinator managed
      // or independent and is Support at Home funded
      // show terms and conditions popup only when organisation has terms and conditions already otherwise continue to send agreement
      if (
        terms?.status !== 'accepted' &&
        this.agreement?.otherUser?.accountType === 'Consumer' &&
        terms?.isShowingTermPopup &&
        terms?.contentOfTerms
      ) {
        this.displayTermsModal(terms);
      } else {
        this.hideModal();
      }
    }
  }

  /**
   * Hide modal
   */
  private hideModal(): void {
    const formValues = JSON.parse(JSON.stringify(this.form.value));
    this.modalCtrl.dismiss(formValues);
  }

  /**
   * Open terms and conditions modal popup
   *
   * @param terms - Contains Organisation modal and pass details into TermsAndConditions modal
   */
  async displayTermsModal(terms: Organisation): Promise<void> {
    const termsModal = await this.modalCtrl.create({
      component: TermsAndConditionsComponent,
      componentProps: {
        terms,
        action: this.agreement.conversation?.hasAgreement ? 'edit' : 'add',
        isAgreementRequested: this.agreement.status === 'requested',
      },
    });

    // https://github.com/ionic-team/ionic-framework/issues/26872
    termsModal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

    await termsModal.present();
    const { data } = await termsModal.onDidDismiss();
    // Allow to send offer only when either the terms were accpeted
    // or were cancelled while amending existing agreement that was not requested by the client
    // because in while offering new agreement or ameneding the requested agreement restricting
    // SP to continue only after accepting organisation terms.
    if (
      data === 'accepted' ||
      (data === 'cancelled' && this.agreement?.conversation?.hasAgreement && this.agreement.status !== 'requested')
    ) {
      setTimeout(() => {
        this.hideModal();
      }, 300);
    }
  }

  /**
   * Private utility members
   *
   * @param agreement
   */
  private amendInfoFor(agreement: Agreement): Agreement {
    if (agreement.agreementAmendment) {
      const amendment = agreement.agreementAmendment;

      agreement.agreedService = amendment.agreedService;
      agreement.agreementRates = amendment.agreementRates;
      agreement.daysAndTimes = amendment.daysAndTimes;
      agreement.jobFrequency = amendment.jobFrequency;
      agreement.jobOccurrence = amendment.jobOccurrence;
      agreement.expectedStartDate = amendment.expectedStartDate;
      agreement.expectedEndDate = amendment.expectedEndDate;
      agreement.scheduleDescription = amendment.scheduleDescription;
    }

    return agreement;
  }
}
